import { Box } from '@primer/react';
import { FinalImpactScoreType, NetScoreFormat } from '@upright/common/model/output/scores/types';
import { AggregateMetricProps } from '@upright/ui/common/upright/model/output/scores/aggregate-metrics';
import { formatNetScore } from '@upright/ui/common/upright/model/output/scores/net-impact-chart/value-formatter';
import BulbSparkIcon from '@upright/ui/icons/img/manual/Ai-Technology-Spark--Streamline-Core.svg';
import { ReactFragment } from 'react';
import { useNetImpactLensContext } from '../../../common/use-net-impact-lens-context';
import { useNetScoreWithoutValueSet } from '../use-net-score';
import { ModelEntityType } from '@upright/common/model/input/types';
import { useTheme } from '@upright/ui/common/generic/theme/custom-theme';

export const ImpactSummaryTextWithIcon = (
  props: { entityType: ModelEntityType | undefined } & AggregateMetricProps,
) => {
  const theme = useTheme().theme;

  const netScore = useNetScoreWithoutValueSet(props.currentMetric);
  const value = netScore?.value;
  const context = useNetImpactLensContext();
  const options = context?.options;
  const { formatters, scoreUnit = FinalImpactScoreType.RELATIVE } = options ?? {};
  const netScoreFormat = props.currentMetric;

  if (!value || !formatters || !props.entityType) return <></>;
  const formattedScore = formatNetScore(value, netScoreFormat, formatters, scoreUnit);
  const isGroup = [ModelEntityType.GROUP, ModelEntityType.PORTFOLIO].includes(props.entityType);
  const text = isGroup ? 'Fund holdings create' : 'Creates';

  return (
    <Box sx={{ display: 'flex', alignItems: 'start', gap: 2, maxWidth: '50%' }}>
      <Box pt={'2px'}>
        <BulbSparkIcon width={14} height={14} fill={theme?.colors.icon.dark} />
      </Box>
      <Box as="span">
        {text} on average
        <Box
          as="span"
          sx={{ fontWeight: 'bold', color: value < 0 ? 'impact.negative.text.dark' : 'impact.positive.text.dark' }}
        >
          {' '}
          <Box as="span">
            {customFriendlyNetScore(formattedScore)}{' '}
            {netScoreFormat === NetScoreFormat.NET_SUM && customFriendlyShortScoreUnit(scoreUnit)}
          </Box>{' '}
          <Box as="span">{value < 0 ? 'more' : 'less'}</Box> negative impact than positive impact
        </Box>
      </Box>
    </Box>
  );
};

function customFriendlyNetScore(
  formattedScore: string | number | boolean | JSX.Element | ReactFragment | null | undefined,
) {
  return formattedScore?.toString().replace('−', '').replace('+', '');
}

function customFriendlyShortScoreUnit(unit: FinalImpactScoreType) {
  switch (unit) {
    case FinalImpactScoreType.CENTS_PER_DOLLAR:
      return '¢/$';
    case FinalImpactScoreType.DOLLARS_PER_YEAR:
      return '$/year';
    case FinalImpactScoreType.RELATIVE:
      return 'units';
    default:
      return unit;
  }
}
